/**
 * One in-flight request per identity, with a short reuse window.
 *
 * For a read that several components ask for at once and that the server answers PER CALLER. Two things
 * follow from that and neither is optional. Components mounting together must share one request, or an
 * endpoint that resolves scopes and permissions before answering is asked three times for one screen. And
 * an answer must never outlive the identity it was given to: signing out navigates without reloading and
 * the company switch happens in-tab, so an unkeyed cache hands the next caller the previous one's rows.
 *
 * Not a general HTTP cache. It holds exactly one value — the last answer for one scope — because that is
 * what a "who may I work with" read is: a single list, invalidated by writes on the same screen rather
 * than by a key space.
 */
/**
 * The answer arrived for an identity nobody is asking about any more.
 *
 * Its own type because it is NOT a failure: nothing broke, the operator switched company or signed out
 * while the read was in flight. Reported as an outage it would file a warning on every such switch, and
 * the error channel would show outages that never happened. Nobody wants this answer, so nothing is
 * retried — the caller for that identity is gone.
 *
 * An INVALIDATION is the other way a read is overtaken, and it is not this: the caller still wants the
 * list, they want the one that includes the save. That case is re-fetched below rather than refused.
 */
export class ScopedRequestSuperseded extends Error {
    constructor() {
        super('scoped-request-cache: the identity changed while this request was in flight')
        this.name = 'ScopedRequestSuperseded'
    }
}

interface ScopedRequestCache<T> {
    /** The answer for this identity, shared with anyone else asking for it right now. */
    load(scope: string): Promise<T>
    /** Drop the answer AND the request in flight, so what returns next cannot predate the write. */
    invalidate(): void
}

interface ScopedRequestCacheOptions {
    /**
     * Called ONCE per failed request, after the retries.
     *
     * The promise is shared, so every waiter's own `.catch` runs on the same failure — reporting from
     * there turns one outage into one report per mounted component. This fires where the request lives.
     */
    onFailure?: (error: unknown) => void
    /** How long an answer is reused before the server is asked again. */
    freshForMs?: number
    /** Retries for the whole request, with `retryAfterMs` between them. A failure is never cached. */
    retries?: number
    retryAfterMs?: number
}

/** How many times one waiter will re-fetch because a save landed mid-read, before taking what it has. */
const MAX_INVALIDATION_REFETCHES = 2

export const createScopedRequestCache = <T>(fetcher: () => Promise<T>, options: ScopedRequestCacheOptions = {}): ScopedRequestCache<T> => {
    const { freshForMs = 30_000, retries = 1, retryAfterMs = 1_500, onFailure } = options
    let cached: { scope: string; value: T; at: number } | null = null
    let inFlight: { scope: string; promise: Promise<T> } | null = null
    /** The identity the last caller asked for. What is in flight is measured against this, not against itself. */
    let currentScope: string | null = null
    /** Bumped by every invalidation, so an answer that left before a write cannot be stored after it. */
    let generation = 0

    const attempt = (left: number): Promise<T> =>
        fetcher().catch((error) => {
            if (left <= 0) throw error
            return new Promise<void>((resolve) => setTimeout(resolve, retryAfterMs)).then(() => attempt(left - 1))
        })

    /**
     * Fetch, and fetch AGAIN if a write invalidated this read while it was in the air.
     *
     * The answer in hand is the list from before the save, and the caller wants the one that includes it.
     * Refusing would turn an ordinary save into an outage for anyone whose read happened to overlap it;
     * resolving would show them the pre-save list as current. Bounded, so a burst of invalidations cannot
     * keep one waiter fetching: past the bound the freshest answer we have is better than none.
     */
    const fetchThroughInvalidations = async (startedAt: number, attemptsLeft: number): Promise<{ value: T; generation: number }> => {
        const value = await attempt(retries)
        if (generation === startedAt || attemptsLeft <= 0) return { value, generation }
        return fetchThroughInvalidations(generation, attemptsLeft - 1)
    }

    return {
        load(scope) {
            // A CHANGE of identity retires everything in flight, not just what is stored. The fetcher takes
            // no arguments — it reads whatever credentials the app currently holds — so a retry that starts
            // under one identity and lands under the next returns the new caller's rows. Stored under the
            // old scope, they would then be served to the old caller on the way back. Bumping the
            // generation here means such an answer is returned to whoever is still waiting for it and never
            // written to the cache.
            if ((cached && cached.scope !== scope) || (inFlight && inFlight.scope !== scope)) {
                cached = null
                inFlight = null
                generation += 1
            }
            if (cached && cached.scope === scope && Date.now() - cached.at < freshForMs) return Promise.resolve(cached.value)
            if (inFlight && inFlight.scope === scope) return inFlight.promise
            currentScope = scope
            const startedAt = generation
            const promise = fetchThroughInvalidations(startedAt, MAX_INVALIDATION_REFETCHES)
                .then(({ value }) => {
                    // Refused, not merely left uncached. A retry runs the argumentless fetcher again, which
                    // reads whatever credentials the app holds BY THEN — so an answer that lands after the
                    // identity changed is the other tenant's rows. Handing it to a caller who is still
                    // waiting is the leak; not storing it only stops the next caller.
                    if (scope !== currentScope) throw new ScopedRequestSuperseded()
                    cached = { scope, value, at: Date.now() }
                    return value
                })
                .catch((error) => {
                    // Skipped for the identity change: the waiter still has to be refused, but nothing
                    // failed, and filing it would put an outage in the error channel for every company
                    // switch made while a read was in flight.
                    if (!(error instanceof ScopedRequestSuperseded)) onFailure?.(error)
                    throw error
                })
                .finally(() => {
                    if (inFlight?.promise === promise) inFlight = null
                })
            inFlight = { scope, promise }
            return promise
        },
        invalidate() {
            cached = null
            inFlight = null
            generation += 1
        },
    }
}
